import type {
  AddTeamMemberBody,
  AdminLevelDefinition,
  AdminOrganization,
  AdminRole,
  AdminSlaPolicy,
  AdminTeam,
  AdminTeamMember,
  AdminTicketCategory,
  AdminTicketStatus,
  AdminTicketSubcategory,
  AdminTicketTypeDefinition,
  AdminUser,
  CatalogList,
  EmailRoutingConfig,
  NotificationAudience,
  NotificationChannel,
  NotificationRulesResponse,
  TeamMembersResponse,
  UpdateEmailRoutingConfigDto,
} from '@central/contracts';
import { api, get, post } from '@/lib/api';

/**
 * Acesso à administração dos catálogos (`/admin/*`).
 *
 * Uma função por rota do servidor, sem nenhuma inventada. Os tipos vêm de
 * `@central/contracts`: a API e a tela leem a mesma declaração, e um campo que
 * mude de forma quebra a compilação em vez de virar `undefined` na tela.
 *
 * Estas rotas são separadas de `/lookups/ticket-form`, que é o vocabulário que
 * qualquer tela de chamado consulta. O que vem daqui carrega o que só
 * administrador tem motivo de ver — a `version` e quantos chamados dependem de
 * cada linha —, e por isso exige a permissão de administração inclusive para
 * ler.
 */

const patch = <T>(caminho: string, corpo: unknown) =>
  api<T>(caminho, { method: 'PATCH', body: corpo });

const remove = (caminho: string) => api<void>(caminho, { method: 'DELETE' });

// ── situações ──────────────────────────────────────────────────────────────

export interface NovaSituacao {
  key: string;
  name: string;
  category: AdminTicketStatus['category'];
  color?: string;
  progressWeight?: number;
  slaClockRunning?: boolean;
}

/**
 * A alteração não tem `key`.
 *
 * Não é esquecimento: o servidor responde 400 a `PATCH { key }`, porque a chave
 * é o que integração, URL e automação usam para achar o registro. O tipo aqui
 * diz a mesma coisa, para o erro aparecer na compilação e não na tela.
 */
export interface AlteracaoDeSituacao {
  name?: string;
  category?: AdminTicketStatus['category'];
  color?: string;
  progressWeight?: number;
  slaClockRunning?: boolean;
  active?: boolean;
  version?: number;
}

export const listarSituacoes = () =>
  get<CatalogList<AdminTicketStatus>>('/admin/ticket-statuses');

export const criarSituacao = (corpo: NovaSituacao) =>
  post<AdminTicketStatus>('/admin/ticket-statuses', corpo);

export const alterarSituacao = (id: string, corpo: AlteracaoDeSituacao) =>
  patch<AdminTicketStatus>(`/admin/ticket-statuses/${id}`, corpo);

export const apagarSituacao = (id: string) => remove(`/admin/ticket-statuses/${id}`);

export const reordenarSituacoes = (ids: readonly string[]) =>
  post<CatalogList<AdminTicketStatus>>('/admin/ticket-statuses/reorder', { ids });

// ── categorias ─────────────────────────────────────────────────────────────

export interface NovaCategoria {
  key: string;
  name: string;
  description?: string;
  color?: string;
}

export interface AlteracaoDeCategoria {
  name?: string;
  description?: string;
  color?: string;
  active?: boolean;
  version?: number;
}

export const listarCategorias = () =>
  get<CatalogList<AdminTicketCategory>>('/admin/ticket-categories');

export const criarCategoria = (corpo: NovaCategoria) =>
  post<AdminTicketCategory>('/admin/ticket-categories', corpo);

export const alterarCategoria = (id: string, corpo: AlteracaoDeCategoria) =>
  patch<AdminTicketCategory>(`/admin/ticket-categories/${id}`, corpo);

export const apagarCategoria = (id: string) => remove(`/admin/ticket-categories/${id}`);

export const reordenarCategorias = (ids: readonly string[]) =>
  post<CatalogList<AdminTicketCategory>>('/admin/ticket-categories/reorder', { ids });

// ── subcategorias ──────────────────────────────────────────────────────────

export interface NovaSubcategoria {
  categoryId: string;
  key: string;
  name: string;
  description?: string;
}

/**
 * `categoryId` também fica de fora da alteração.
 *
 * Mover a subcategoria mudaria o significado dos chamados que já apontam para
 * ela sem tocar em nenhum deles. Quem precisa disso cria no lugar certo e
 * desativa a antiga — operação que deixa rastro.
 */
export interface AlteracaoDeSubcategoria {
  name?: string;
  description?: string;
  active?: boolean;
  version?: number;
}

export const listarSubcategorias = () =>
  get<CatalogList<AdminTicketSubcategory>>('/admin/ticket-subcategories');

export const criarSubcategoria = (corpo: NovaSubcategoria) =>
  post<AdminTicketSubcategory>('/admin/ticket-subcategories', corpo);

export const alterarSubcategoria = (id: string, corpo: AlteracaoDeSubcategoria) =>
  patch<AdminTicketSubcategory>(`/admin/ticket-subcategories/${id}`, corpo);

export const apagarSubcategoria = (id: string) => remove(`/admin/ticket-subcategories/${id}`);

export const reordenarSubcategorias = (ids: readonly string[]) =>
  post<CatalogList<AdminTicketSubcategory>>('/admin/ticket-subcategories/reorder', { ids });

// ── equipes ────────────────────────────────────────────────────────────────

export interface NovaEquipe {
  key: string;
  name: string;
  type?: AdminTeam['type'];
  email?: string;
  supportTier?: number;
  escalatesToTeamId?: string;
}

export interface AlteracaoDeEquipe {
  name?: string;
  type?: AdminTeam['type'];
  email?: string;
  active?: boolean;
  version?: number;
  supportTier?: number | null;
  escalatesToTeamId?: string | null;
}

export const listarEquipes = () => get<CatalogList<AdminTeam>>('/admin/teams');

export const criarEquipe = (corpo: NovaEquipe) => post<AdminTeam>('/admin/teams', corpo);

export const alterarEquipe = (id: string, corpo: AlteracaoDeEquipe) =>
  patch<AdminTeam>(`/admin/teams/${id}`, corpo);

export const apagarEquipe = (id: string) => remove(`/admin/teams/${id}`);

export const listarMembros = (teamId: string) =>
  get<TeamMembersResponse>(`/admin/teams/${teamId}/members`);

export const adicionarMembro = (teamId: string, corpo: AddTeamMemberBody) =>
  post<AdminTeamMember>(`/admin/teams/${teamId}/members`, corpo);

export const removerMembro = (teamId: string, userId: string) =>
  remove(`/admin/teams/${teamId}/members/${userId}`);

// ── organizações ───────────────────────────────────────────────────────────

export interface NovaOrganizacao {
  legalName: string;
  tradeName?: string;
  taxId?: string;
  domain?: string;
  type?: AdminOrganization['type'];
}

export interface AlteracaoDeOrganizacao extends Omit<Partial<NovaOrganizacao>, 'tradeName' | 'taxId' | 'domain'> {
  /** `null` limpa o campo. */
  tradeName?: string | null;
  taxId?: string | null;
  domain?: string | null;
  active?: boolean;
  version?: number;
}

export const listarOrganizacoes = () =>
  get<CatalogList<AdminOrganization>>('/admin/organizations');

export const criarOrganizacao = (corpo: NovaOrganizacao) =>
  post<AdminOrganization>('/admin/organizations', corpo);

export const alterarOrganizacao = (id: string, corpo: AlteracaoDeOrganizacao) =>
  patch<AdminOrganization>(`/admin/organizations/${id}`, corpo);

export const apagarOrganizacao = (id: string) => remove(`/admin/organizations/${id}`);

// ── notificações ───────────────────────────────────────────────────────────

export const listarNotificacoes = () =>
  get<NotificationRulesResponse>('/admin/notifications');

export const alterarNotificacao = (
  eventType: string,
  audience: NotificationAudience,
  channel: NotificationChannel,
  enabled: boolean,
) => patch<unknown>(`/admin/notifications/${eventType}/${audience}/${channel}`, { enabled });

// ── operadores ─────────────────────────────────────────────────────────────

export interface NovoOperador {
  email: string;
  name: string;
  displayName?: string;
  jobTitle?: string;
  phone?: string;
}

export interface AlteracaoDeOperador {
  name?: string;
  displayName?: string;
  jobTitle?: string;
  phone?: string;
  status?: AdminUser['status'];
  version?: number;
}

export const listarOperadores = () => get<{ items: AdminUser[] }>('/admin/users');

export const criarOperador = (corpo: NovoOperador) => post<AdminUser>('/admin/users', corpo);

export const alterarOperador = (id: string, corpo: AlteracaoDeOperador) =>
  patch<AdminUser>(`/admin/users/${id}`, corpo);

export const apagarOperador = (id: string) => remove(`/admin/users/${id}`);

export const atribuirPapel = (userId: string, roleId: string) =>
  post<AdminUser>(`/admin/users/${userId}/roles`, { roleId });

export const revogarPapel = (userId: string, assignmentId: string) =>
  remove(`/admin/users/${userId}/roles/${assignmentId}`);

// ── papéis ─────────────────────────────────────────────────────────────────

export const listarPapeis = () => get<{ items: AdminRole[] }>('/admin/roles');

// ── políticas de SLA ───────────────────────────────────────────────────────

export interface NovaPoliticaDeSla {
  key: string;
  name: string;
  kind: AdminSlaPolicy['kind'];
  metric: AdminSlaPolicy['metric'];
  targetMinutes: number;
  warnThreshold?: number;
  priorityOrder?: number;
  contractual?: boolean;
}

export interface AlteracaoDePoliticaDeSla {
  name?: string;
  targetMinutes?: number;
  warnThreshold?: number;
  priorityOrder?: number;
  contractual?: boolean;
  active?: boolean;
  version?: number;
}

export const listarPoliticasSla = () => get<{ items: AdminSlaPolicy[] }>('/admin/sla-policies');

export const criarPoliticaSla = (corpo: NovaPoliticaDeSla) =>
  post<AdminSlaPolicy>('/admin/sla-policies', corpo);

export const alterarPoliticaSla = (id: string, corpo: AlteracaoDePoliticaDeSla) =>
  patch<AdminSlaPolicy>(`/admin/sla-policies/${id}`, corpo);

export const apagarPoliticaSla = (id: string) => remove(`/admin/sla-policies/${id}`);

// ── campos configuráveis de chamado ────────────────────────────────────────

export interface AlteracaoDeDefinicao {
  label?: string;
  color?: string | null;
  order?: number;
  active?: boolean;
  version: number;
}

export interface AlteracaoDeTipoDeChamado {
  label?: string;
  order?: number;
  active?: boolean;
  version: number;
}

export const listarPrioridades = () =>
  get<{ items: AdminLevelDefinition[] }>('/admin/priority-definitions');

export const alterarPrioridade = (id: string, corpo: AlteracaoDeDefinicao) =>
  patch<AdminLevelDefinition>(`/admin/priority-definitions/${id}`, corpo);

export const listarUrgencias = () =>
  get<{ items: AdminLevelDefinition[] }>('/admin/urgency-definitions');

export const alterarUrgencia = (id: string, corpo: AlteracaoDeDefinicao) =>
  patch<AdminLevelDefinition>(`/admin/urgency-definitions/${id}`, corpo);

export const listarImpactos = () =>
  get<{ items: AdminLevelDefinition[] }>('/admin/impact-definitions');

export const alterarImpacto = (id: string, corpo: AlteracaoDeDefinicao) =>
  patch<AdminLevelDefinition>(`/admin/impact-definitions/${id}`, corpo);

export const listarTiposDeChamado = () =>
  get<{ items: AdminTicketTypeDefinition[] }>('/admin/ticket-type-definitions');

export const alterarTipoDeChamado = (id: string, corpo: AlteracaoDeTipoDeChamado) =>
  patch<AdminTicketTypeDefinition>(`/admin/ticket-type-definitions/${id}`, corpo);

// ── chaves do TanStack Query ───────────────────────────────────────────────

/**
 * Uma chave por catálogo, todas sob a mesma raiz.
 *
 * Depois de escrever, invalida-se a raiz inteira: mexer numa categoria muda a
 * contagem de uso da subcategoria e o vocabulário que a tela de chamado lê, e
 * enumerar cada chave afetada seria uma lista para esquecer de atualizar.
 */
// ── roteamento de e-mail ──────────────────────────────────────────────────

export const obterRoteamentoDeEmail = () =>
  get<EmailRoutingConfig>('/admin/email-routing');

export const salvarRoteamentoDeEmail = (dto: UpdateEmailRoutingConfigDto) =>
  api<EmailRoutingConfig>('/admin/email-routing', { method: 'PUT', body: dto });

// ── chaves do TanStack Query ───────────────────────────────────────────────

export const chavesDeAdministracao = {
  raiz: ['administracao'] as const,
  situacoes: ['administracao', 'situacoes'] as const,
  categorias: ['administracao', 'categorias'] as const,
  subcategorias: ['administracao', 'subcategorias'] as const,
  equipes: ['administracao', 'equipes'] as const,
  membrosDeEquipe: (teamId: string) => ['administracao', 'equipes', teamId, 'membros'] as const,
  organizacoes: ['administracao', 'organizacoes'] as const,
  notificacoes: ['administracao', 'notificacoes'] as const,
  operadores: ['administracao', 'operadores'] as const,
  papeis: ['administracao', 'papeis'] as const,
  politicasSla: ['administracao', 'politicas-sla'] as const,
  prioridades: ['administracao', 'prioridades'] as const,
  urgencias: ['administracao', 'urgencias'] as const,
  impactos: ['administracao', 'impactos'] as const,
  tiposDeChamado: ['administracao', 'tipos-de-chamado'] as const,
  roteamentoDeEmail: ['administracao', 'roteamento-email'] as const,
};
